import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowLeft, LogOut, Menu, ShieldCheck, ShoppingBag, User as UserIcon, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { BrandLockup } from "./BrandLockup";
import { DesktopHeaderNav } from "./DesktopHeaderNav";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { CurrencySwitcher } from "./CurrencySwitcher";
import { LocalePicker } from "./LocalePicker";

import { SearchDialog } from "./SearchDialog";
import { useAuth, signOutAndGoHome } from "@/hooks/use-auth";
import { AccountMenu } from "@/components/site/AccountMenu";
import { BuenFinNotice } from "./BuenFinCampaign";
import { Button } from "@/components/ui/button";

interface Props {
  /** Floating glass header — no solid background, no border. Used on the Maya hero. */
  transparent?: boolean;
  /** Minimal mode: only Logo, language, login, Reservar. Hides full nav links. */
  minimal?: boolean;
  /** Show a back-to-home button on mobile, next to the cart icon. */
  backHome?: boolean;
  compactHome?: boolean;
}

export function SiteHeader({ transparent = false, minimal = false, backHome = false, compactHome = false }: Props) {
  const { t, i18n } = useTranslation();
  const loginLabel = i18n.language.startsWith("en") ? "Sign in" : "Iniciar sesión";
  const cartLabel = i18n.language.startsWith("en") ? "Cart" : "Carrito";
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, isAdmin } = useAuth();
  const menuRef = useRef<HTMLDivElement>(null);

  const nav = [
    { to: "/explora", label: t("nav.explora", "Explora México") },
    { to: "/tours", label: t("nav.tours") },
    { to: "/verde", label: "México Verde" },
    { to: "/maya", label: t("nav.bottom.audioguide", "Audioguía") },
    { to: "/blog", label: t("nav.inspiration") },
  ];

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenu(false);
    }
    if (menu) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menu]);

  useEffect(() => {
    if (!transparent) return;
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparent]);

  // Al volver de segundo plano (bloqueo/desbloqueo en Android, cambio de app),
  // cerrar menús efímeros y re-evaluar el scroll para que el header no quede
  // en un estado visual desincronizado.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      setOpen(false);
      setMenu(false);
      setSearchOpen(false);
      if (transparent) setScrolled(window.scrollY > 24);
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
  }, [transparent]);

  // Color palettes: transparent (over dark hero) vs default (light page)
  const isGlass = transparent && !scrolled;
  const wrapperCls = transparent
    ? `fixed top-0 z-50 w-full transition-all duration-500 ${
        scrolled ? "bg-background/85 backdrop-blur-xl border-b border-border/60" : "bg-transparent"
      }`
    : "sticky top-0 z-50 w-full border-b border-border/60 bg-background/85 backdrop-blur-md";

  const textCls = isGlass ? "text-white/85" : "text-foreground/75";
  const hoverChipCls = isGlass
    ? "hover:bg-white/10 hover:text-white"
    : "hover:bg-accent hover:text-accent-foreground";
  const iconBtnCls = isGlass
    ? "text-white/80 hover:bg-white/10 hover:text-white"
    : "text-foreground/70 hover:bg-accent";

  return (
    <><header className={wrapperCls}>
      <div
        className={`mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8 ${
          compactHome ? "home-desktop-header h-16 w-full" : transparent ? "h-14 max-w-[1400px]" : "h-16 max-w-7xl"
        }`}
      >
        <div className="flex items-center gap-6">
          <BrandLockup />
          <nav
            className={`gap-0.5 hidden ${minimal ? "hidden" : "hidden"}`}
            style={{
              opacity: minimal ? 0 : 1,
              transform: minimal ? "translateY(-6px)" : "translateY(0)",
              pointerEvents: minimal ? "none" : "auto",
              transition: "opacity 500ms ease, transform 500ms ease",
              display: minimal ? "none" : undefined,
            }}
          >
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className={`rounded-full px-3 py-1.5 text-[13px] font-medium transition-colors ${textCls} ${hoverChipCls}`}
                activeProps={{
                  className: isGlass
                    ? "bg-white/15 text-white"
                    : "bg-accent text-accent-foreground",
                }}
              >
                {n.label}
              </Link>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-1.5">
          {/* md/tablet: switchers separados (comportamiento actual).
              lg+: un único selector compacto “🌐 ES · MXN”. */}
          <div className="hidden">
            <LanguageSwitcher />
            <CurrencySwitcher />
          </div>
          <DesktopHeaderNav compactHome={compactHome} />
          <div className="hidden lg:flex">
            <LocalePicker />
          </div>
          {compactHome && <Button asChild size="sm" className="hidden lg:inline-flex"><Link to="/tours">{t("nav.book")}</Link></Button>}


          {(!minimal || user) && (
            <AccountMenu
              triggerClassName={`inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors lg:hidden ${iconBtnCls}`}
            />
          )}

          {!minimal && (
            <Link
              to="/cart"
              aria-label={cartLabel}
              className={`relative inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors ${iconBtnCls}`}
            >
              <ShoppingBag className="h-4 w-4" />
            </Link>
          )}
          {backHome && (
            <Link
              to="/"
              aria-label={t("nav.backHome", "Volver al inicio")}
              title={t("nav.backHome", "Volver al inicio")}
              className={`inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors lg:hidden ${iconBtnCls}`}
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>
          )}

          {!user && (
            <Link
              to="/auth"
              aria-label={loginLabel}
              className={`hidden h-9 items-center rounded-full px-3 text-[13px] font-semibold transition-colors hidden ${
                isGlass
                  ? "text-white/85 hover:bg-white/10"
                  : "text-foreground hover:bg-accent border border-border"
              }`}
            >
              {t("nav.login")}
            </Link>
          )}

          <Link
            to="/tours"
            className={`hidden h-9 items-center rounded-full px-4 text-[13px] font-semibold transition-all hidden ${
              isGlass
                ? "metal-orange shadow-[0_8px_28px_-8px_rgba(255,138,31,0.55)] hover:brightness-110"
                : "metal-orange shadow-[0_8px_20px_-6px_rgba(255,90,0,0.5)] hover:scale-[1.02]"
            }`}
          >
            {t("nav.book")}
          </Link>
          {!minimal && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label={t("nav.menu")}
              className={`inline-flex h-9 w-9 items-center justify-center rounded-full lg:hidden ${
                isGlass ? "text-white/90 hover:bg-white/10" : ""
              }`}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          )}
        </div>
      </div>
      {open && (
        <div className={`lg:hidden ${isGlass ? "border-t border-white/10 bg-black/70 backdrop-blur-xl" : "border-t border-border/60 bg-background"}`}>
          <nav className={`mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3 ${isGlass ? "text-white" : ""}`}>
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className={`rounded-lg px-4 py-3 text-base font-medium ${isGlass ? "text-white/85 hover:bg-white/10" : "text-foreground/80 hover:bg-accent"}`}
              >
                {n.label}
              </Link>
            ))}
            <div className="px-2 py-2">
              <LanguageSwitcher variant="full" />
              <div className="mt-3"><CurrencySwitcher variant="full" /></div>
            </div>
            {user ? (
              <>
                <Link to="/mi-cuenta" onClick={() => setOpen(false)} className={`rounded-lg px-4 py-3 text-base font-medium ${isGlass ? "text-white/85 hover:bg-white/10" : "text-foreground/80 hover:bg-accent"}`}>
                  {t("nav.myAccount")}
                </Link>
                {isAdmin && (
                  <Link to="/admin/dashboard" onClick={() => setOpen(false)} className={`rounded-lg px-4 py-3 text-base font-medium ${isGlass ? "text-white/85 hover:bg-white/10" : "text-foreground/80 hover:bg-accent"}`}>
                    {t("nav.admin")}
                  </Link>
                )}
                <button
                  type="button"
                  onClick={async () => {
                    setOpen(false);
                    await signOutAndGoHome();
                  }}
                  className="flex items-center gap-2 rounded-lg px-4 py-3 text-left text-base font-semibold text-destructive hover:bg-accent"
                >
                  <LogOut className="h-5 w-5" /> {t("nav.logout")}
                </button>
              </>
            ) : (
              <Link to="/auth" aria-label={loginLabel} onClick={() => setOpen(false)} className={`rounded-lg px-4 py-3 text-base font-medium ${isGlass ? "text-white/85 hover:bg-white/10" : "text-foreground/80 hover:bg-accent"}`}>
                {t("nav.login")}
              </Link>
            )}
            <Link
              to="/tours"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center rounded-lg bg-primary px-4 py-3 text-base font-semibold text-primary-foreground"
            >
              {t("nav.book")}
            </Link>
          </nav>
        </div>
      )}
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </header><BuenFinNotice /></>
  );
}
